// Resource paths (resources.md §2.2, ADR-023): every operation has exactly one URL.
import { kindInfo } from "./kinds";

const enc = encodeURIComponent;

/**
 * The collection URL. A project-scoped kind without a project lists across projects (`-A`, list and watch only);
 * org-scoped kinds take their org from the credential or the `Nodus-Org` header, never from the path.
 */
export function collectionPath(kind: string, project?: string): string {
  const info = kindInfo(kind);
  const base = `/apis/nodus.dev/${info.version}`;
  if (info.scope === "project" && project) return `${base}/namespaces/${enc(project)}/${info.resource}`;
  return `${base}/${info.resource}`;
}

export function objectPath(kind: string, name: string, project?: string): string {
  const info = kindInfo(kind);
  if (info.scope === "project" && !project) throw new Error(`${kind} is project-scoped: a project is required`);
  return `${collectionPath(kind, project)}/${enc(name)}`;
}

export function subresourcePath(kind: string, name: string, subresource: string, project?: string, id?: string): string {
  const info = kindInfo(kind);
  if (!info.subresources.includes(subresource)) throw new Error(`${kind} has no ${subresource} subresource`);
  return `${objectPath(kind, name, project)}/${subresource}${id ? `/${enc(id)}` : ""}`;
}

/** Whether a request to this subresource can wake stopped compute (ui-spec §4.16). */
export function wakes(kind: string, subresource: string): boolean {
  return kindInfo(kind).wakes?.includes(subresource) ?? false;
}

/** Non-resource endpoints the shell calls (resources.md §2.2). */
export const endpoints = {
  whoami: "/auth/whoami",
  invites: "/auth/invites",
  invitesAccept: "/auth/invites/accept",
  invitesLookup: "/auth/invites/lookup",
  joinableOrgs: "/auth/joinable-orgs",
  joinOrg: "/auth/joinable-orgs/join",
  cliKeys: "/auth/cli-keys",
  deviceApprove: "/auth/device/approve",
  deviceDeny: "/auth/device/deny",
  meSettings: "/auth/me/settings",
  runOutcomes: (project: string) => `/metrics/v1/namespaces/${enc(project)}/runs`,
  agentAuthorization: (id: string) => `/v1/console/agent-authorizations/${enc(id)}`,
} as const;

/** The client-writable request actions (resources.md §2.3, ADR-085); `nodus request <action> kind/name` sets the same. */
export type RequestAction = "restart" | "refresh-secrets" | "reload-sinks" | "verify" | "resend" | "start" | "reimport" | "approve-burst";

/**
 * The merge-patch body for a request action. The controller acts once per distinct time it has not handled, so a
 * fresh RFC 3339 time is what makes a repeated click a new request rather than a no-op.
 */
export function requestPatch(action: RequestAction, at: Date = new Date()): { metadata: { annotations: Record<string, string> } } {
  return { metadata: { annotations: { [`nodus.dev/${action}-requested-at`]: at.toISOString() } } };
}
